/**
 * Helpers for WordPress URLs surfaced in the Content Planner.
 *
 * A `wp_url` coming back from the backend can be a *draft preview* link
 * (e.g. `https://site.com/?p=123&preview=true&preview_id=123&preview_nonce=ab12`)
 * rather than the public, published permalink. Linking to that from a "live"
 * badge is misleading — preview URLs require WP auth, can expire, and never
 * represent the live page. These helpers let the UI tell the two apart and
 * surface a clean public URL for genuinely-published posts.
 */

/** Query params WordPress appends to a preview/autosave link. */
const PREVIEW_PARAMS = ['preview', 'preview_id', 'preview_nonce', 'preview_uuid'];

/** True when the URL carries WordPress preview/autosave query params. */
export function isPreviewUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return /[?&](preview|preview_id|preview_nonce|preview_uuid)=/i.test(url);
}

/**
 * Return the public permalink for a WordPress URL by removing only the
 * preview-specific query params. We deliberately keep `p` / `page_id` so that
 * sites using plain permalinks (where `?p=123` *is* the live URL) still work.
 * Falls back to the original string if the URL can't be parsed.
 */
export function cleanWpUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    PREVIEW_PARAMS.forEach((param) => u.searchParams.delete(param));
    return u.toString();
  } catch {
    return url;
  }
}
